import { useMemo, useRef } from 'react';
import { DirectionalHint, IDropdownOption, IRenderFunction, ISelectableOption, TooltipHost, IconButton, IButtonStyles, Stack, Text } from '@fluentui/react';
import { DataverseEnvironment } from '../../../../../../api/generated/data-contracts';
import { useQuery } from '@tanstack/react-query';
import { INTEGRATION_DATAVERSE_ENV_LIST } from '../../../../../../constants/queryKeys';
import { fromMinutes } from '../../../../../../_helpers/milliseconds';
import { ApiCalls } from '../../../../../../api/api';
import { FilterDropdown } from '../../../../../../_components/FilterDropdown/FilterDropdown';
import { useId } from '@fluentui/react-hooks';

export const EnvironmentDropdown = ({
    onChange,
    defaultSelectedKey,
    disabled,
}: {
    onChange?: (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption<DataverseEnvironment>, index?: number) => void;
    defaultSelectedKey?: string;
    disabled?: boolean;
}) => {
    const defaultEnvs = useRef([] as IDropdownOption<DataverseEnvironment>[]).current;
    const { data: dataverseEnvironmentOptions = defaultEnvs, isLoading } = useQuery({
        queryKey: [INTEGRATION_DATAVERSE_ENV_LIST],
        queryFn: () =>
            ApiCalls.dataverseEnvironmentsList().then(res =>
                res.data.map(env => {
                    return {
                        key: env.url,
                        text: env.url,
                        data: env,
                    } as IDropdownOption<DataverseEnvironment>;
                }),
            ),
        staleTime: fromMinutes(1),
    });
    const tooltipTriggerId = useId('tooltipTrigger');
    const TooltipContent = useMemo(() => {
        return (
            <Stack tokens={{ childrenGap: 12 }} styles={{ root: { maxWidth: 300, textAlign: 'left', padding: '10px 20px' } }}>
                <Text variant="small">To add the Time for Teams application as an App user in the desired Dataverse Environment, follow these steps:</Text>
                <Stack tokens={{ childrenGap: 5 }}>
                    <Text variant="small">1. Go to the Dataverse Admin Center.</Text>
                    <Text variant="small">2. Choose the environment you want to integrate with.</Text>
                    <Text variant="small">3. Navigate to the "Application Users" section in the Settings area.</Text>
                    <Text variant="small">
                        4. Add a new user and copy-paste the Application (Client) ID into the search field to add the Time for Teams app to the environment.
                    </Text>
                    <Text variant="small">5. Assign a security role to the application user.</Text>
                </Stack>
                <Text variant="small">Please note, there can be a delay of up to 15 minutes for the changes to take effect.</Text>
            </Stack>
        );
    }, []);

    return (
        <div style={{ position: 'relative' }}>
            <TooltipHost content={TooltipContent} calloutProps={{ directionalHint: DirectionalHint.topCenter, target: `#${tooltipTriggerId}` }}>
                <IconButton iconProps={{ iconName: 'Info' }} id={tooltipTriggerId} styles={iconButtonStyle} />
            </TooltipHost>
            <FilterDropdown
                label="Url"
                required
                options={dataverseEnvironmentOptions}
                defaultSelectedKey={defaultSelectedKey}
                onRenderOption={onRenderOption}
                filterFn={urlDropdownFilter}
                onChange={onChange}
                placeholder={isLoading ? 'Loading list...' : 'Select environment'}
                disabled={disabled || isLoading}
            />
        </div>
    );
};

const onRenderOption: IRenderFunction<ISelectableOption<DataverseEnvironment>> = (props, defaultRender) => {
    if (defaultRender && props) {
        return (
            <TooltipHost styles={{ root: { display: 'inline-block' } }} content={props.data.friendlyName} directionalHint={DirectionalHint.topLeftEdge}>
                {defaultRender(props)}
            </TooltipHost>
        );
    }
    return null;
};

const urlDropdownFilter = (option: IDropdownOption<DataverseEnvironment>, filterText: string) => {
    const text = filterText.toLowerCase();
    return option.data.url.toLowerCase().includes(text) || option.data.friendlyName.toLowerCase().includes(text);
};

const iconButtonStyle: IButtonStyles = {
    root: {
        width: 20,
        height: 20,
        position: 'absolute',
        top: 5,
        right: 0,
    },
    rootHovered: {
        backgroundColor: 'transparent',
        color: 'rgb(96, 97, 170)',
    },
    icon: {
        fontSize: 14,
    },
};
